import { Fragment, useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  api,
  Balanza,
  Cliente,
  CongruenciaGlobal,
  CfdiDelPanel,
  CuentaDeCatalogo,
  ErrorDeApi,
  EtiquetaFiscalMovimiento,
  MetodoDePago,
  Poliza,
  ResumenDePanel,
  ResumenPeriodo,
  TipoPoliza,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'
import { AyudaDeCampo } from '../ayuda/AyudaDeCampo'

const ETIQUETAS_FISCALES: { valor: EtiquetaFiscalMovimiento; texto: string }[] = [
  { valor: 'TrasladoIva', texto: 'IVA trasladado' },
  { valor: 'RetencionIsr', texto: 'ISR retenido' },
  { valor: 'RetencionIva', texto: 'IVA retenido' },
]

const METODOS_DE_PAGO: { valor: MetodoDePago; texto: string }[] = [
  { valor: 'Transferencia', texto: 'Transferencia' },
  { valor: 'Cheque', texto: 'Cheque' },
  { valor: 'Efectivo', texto: 'Efectivo' },
]

/**
 * Bancos más comunes del catálogo c_Banco del SAT (RF-803, PL del Anexo 24): compilado en el
 * front, igual que en ContaQuick.Domain.Comun.CatalogoBancosSat del backend. Solo se usa para
 * resolver el código con Cheque/Transferencia — "Banco" sigue siendo el nombre libre de siempre.
 */
const BANCOS_SAT: { codigo: string; texto: string }[] = [
  { codigo: '002', texto: 'Banamex' },
  { codigo: '012', texto: 'BBVA México' },
  { codigo: '014', texto: 'Santander' },
  { codigo: '021', texto: 'HSBC' },
  { codigo: '036', texto: 'Inbursa' },
  { codigo: '044', texto: 'Scotiabank' },
  { codigo: '058', texto: 'Banregio' },
  { codigo: '059', texto: 'Invex' },
  { codigo: '062', texto: 'Afirme' },
  { codigo: '072', texto: 'Banorte' },
  { codigo: '127', texto: 'Banco Azteca' },
  { codigo: '137', texto: 'BanCoppel' },
  { codigo: '143', texto: 'CIBanco' },
  { codigo: '999', texto: 'No identificado / otros' },
]

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Fecha de póliza en corto: «02 ago». Se arma con los pedazos de la cadena en vez de con
 * `new Date`, por dos razones: `new Date('2026-08-02')` es medianoche UTC, que en México cae
 * el día anterior y recorrería toda la columna; y `toLocaleDateString` con `month: 'short'`
 * devuelve «02-ago» con guion, que no es lo que pide el diseño.
 */
const fechaCorta = (iso: string) => {
  const [, mm, dd] = iso.slice(0, 10).split('-')
  const mes = MESES[Number(mm) - 1]
  return mes ? `${dd} ${mes.slice(0, 3)}` : iso
}

/**
 * Los cuatro estados que puede tener una póliza en la lista. Ninguno viene del backend: se
 * derivan de lo que ya está en pantalla, porque `estado` solo distingue Vigente de Cancelada.
 *
 * - «Corregida» es una cancelada que otra póliza del periodo sustituyó (RF-406): la v1 se
 *   conserva y se puede consultar, que es muy distinto de una cancelación a secas.
 * - «Revisar» es una vigente que ampara un CFDI que el SAT canceló después (RF-505). La
 *   póliza sigue siendo válida; lo que hay que revisar es su respaldo.
 */
export function estadoDePoliza(
  poliza: Poliza,
  sustituidas: Set<string>,
  uuidsCancelados: Set<string>,
): { texto: string; clase: string; titulo: string } {
  if (poliza.estado === 'Cancelada') {
    return sustituidas.has(poliza.id)
      ? { texto: 'Corregida', clase: 'neutra', titulo: 'Se corrigió con una póliza sustituta; esta versión se conserva consultable (RF-406).' }
      : { texto: 'Cancelada', clase: 'vencido', titulo: 'Cancelada con motivo; se conserva íntegra como rastro (RF-406).' }
  }
  if (poliza.cfdis.some((c) => uuidsCancelados.has(c.uuid))) {
    return { texto: 'Revisar', clase: 'urgente', titulo: 'El SAT canceló un CFDI amparado por esta póliza (RF-505).' }
  }
  return { texto: 'Registrada', clase: 'vigente', titulo: 'Vigente, con cargos y abonos cuadrados (RN-01).' }
}

/** UUID de 36 caracteres reducido a sus extremos: completo no cabe en un chip. */
const uuidCorto = (uuid: string) => (uuid.length > 17 ? `${uuid.slice(0, 8)}…${uuid.slice(-4)}` : uuid)

/** Cómo se ve el movimiento en el detalle de solo lectura: moneda, TC y datos de pago si los tiene (RF-403, C-04). */
function detalleDePago(m: {
  moneda: string
  tipoCambio?: number
  metodoDePago?: MetodoDePago
  banco?: string
  cuenta?: string
  referencia?: string
  rfcTercero?: string
  nombreTercero?: string
}) {
  const partes: string[] = []
  if (m.moneda !== 'MXN') partes.push(`${m.moneda}${m.tipoCambio ? ` @ ${m.tipoCambio}` : ''}`)
  if (m.metodoDePago) {
    const datos = [m.metodoDePago, m.banco, m.cuenta, m.referencia].filter(Boolean).join(' ')
    partes.push(datos)
  }
  // RF-803: con RFC y nombre del tercero capturados, el PL puede traer el nodo Cheque/Transferencia.
  if (m.nombreTercero || m.rfcTercero) {
    partes.push(`Tercero: ${[m.nombreTercero, m.rfcTercero].filter(Boolean).join(' · ')}`)
  }
  return partes.join(' · ')
}

/** Pólizas del cliente por periodo (RF-401, RF-402, RF-404 · RN-01, RN-06 · cédula C). */
export function Polizas() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth() + 1)
  const [cliente, setCliente] = useState<Cliente>()
  const [periodos, setPeriodos] = useState<ResumenPeriodo[]>()
  const [polizas, setPolizas] = useState<Poliza[]>()
  const [error, setError] = useState('')
  const avisar = useAvisos()
  const [capturando, setCapturando] = useState(false)
  const [abierta, setAbierta] = useState<Poliza | null>(null)
  const idTituloDetalle = useId()
  const panelDetalleRef = useTrampaDeFoco<HTMLDivElement>(abierta !== null, () => setAbierta(null))
  const [cargandoOriginal, setCargandoOriginal] = useState(false)
  const [cancelando, setCancelando] = useState<Poliza | null>(null)
  const [corrigiendo, setCorrigiendo] = useState<Poliza | null>(null)
  const [confirmandoCierre, setConfirmandoCierre] = useState(false)
  const [pendientes, setPendientes] = useState<number>()
  const [uuidsCancelados, setUuidsCancelados] = useState<Set<string>>(() => new Set())

  const periodoActual = periodos?.find((p) => p.anio === anio && p.mes === mes)
  const cerrado = periodoActual?.estado === 'Cerrado'

  async function recargar() {
    if (!clienteId) return
    try {
      const [datosCliente, listaPeriodos, listaPolizas] = await Promise.all([
        api.obtenerCliente(clienteId),
        api.listarPeriodos(clienteId, anio),
        api.listarPolizas(clienteId, anio, mes),
      ])
      setCliente(datosCliente)
      setPeriodos(listaPeriodos)
      setPolizas(listaPolizas)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las pólizas.')
    }
  }

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, anio, mes])

  // Los CFDI sin contabilizar y las cancelaciones del SAT alimentan la franja y el chip
  // «Revisar», pero ninguno de los dos es la pantalla: van aparte de `recargar` para que un
  // fallo suyo deje la franja incompleta en vez de dejar la lista de pólizas sin cargar.
  useEffect(() => {
    if (!clienteId) return
    let vigente = true
    void (async () => {
      const [panel, alertas] = await Promise.allSettled([
        api.panelResumen(clienteId, anio, mes),
        api.alertasDeCancelacion(clienteId),
      ])
      if (!vigente) return
      setPendientes(panel.status === 'fulfilled' ? panel.value.pendientes : undefined)
      setUuidsCancelados(
        alertas.status === 'fulfilled' ? new Set(alertas.value.map((a) => a.uuid)) : new Set(),
      )
    })()
    return () => {
      vigente = false
    }
  }, [clienteId, anio, mes])

  if (error && !polizas) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente || !polizas) return <p>Cargando…</p>

  /** Navega desde una corrección hacia la póliza original que sustituyó (RF-406, C-06). */
  async function verOriginal(polizaId: string) {
    setCargandoOriginal(true)
    try {
      setAbierta(await api.obtenerPoliza(clienteId!, polizaId))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo abrir la póliza original.')
    } finally {
      setCargandoOriginal(false)
    }
  }

  async function reabrir() {
    try {
      await api.reabrirPeriodo(clienteId!, anio, mes)
      avisar(`Periodo ${MESES[mes - 1]} ${anio} REABIERTO; la reapertura quedó en la bitácora.`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado del periodo.')
    }
  }

  // Las cuatro cifras de la franja salen de las pólizas que ya están en pantalla: sumar aquí
  // evita un endpoint nuevo y, sobre todo, evita que la franja y la tabla puedan discrepar.
  const vigentes = polizas.filter((p) => p.estado === 'Vigente')
  const porTipo = (t: TipoPoliza) => vigentes.filter((p) => p.tipo === t).length
  const cargosDelPeriodo = vigentes.reduce((suma, p) => suma + p.totalCargos, 0)
  const abonosDelPeriodo = vigentes.reduce((suma, p) => suma + p.totalAbonos, 0)
  const cuadra = Math.abs(cargosDelPeriodo - abonosDelPeriodo) < 0.005
  const sinUuid = vigentes.filter((p) => p.cfdis.length === 0).length
  // Una cancelada que otra póliza sustituyó es una corrección, no una cancelación (RF-406).
  const sustituidas = new Set(polizas.map((p) => p.sustituyeAPolizaId).filter((id): id is string => !!id))
  // Periodos del año con movimientos, del más reciente al más viejo. El tope de seis es para
  // que el segmentado no desborde el encabezado en diciembre; a los demás se llega con «Ir a».
  const periodosDelAnio = (periodos ?? []).filter((p) => p.anio === anio).sort((a, b) => b.mes - a.mes).slice(0, 6)

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado">
        <h1>Pólizas</h1>
        <span>
          <Link className="boton" to={`/clientes/${clienteId}/plantillas-poliza`}>Plantillas recurrentes</Link>{' '}
          {periodoActual && (
            <button onClick={() => (cerrado ? void reabrir() : setConfirmandoCierre(true))}>
              {cerrado ? 'Reabrir periodo' : 'Cerrar periodo'}
            </button>
          )}{' '}
          <button className="primario" data-recorrido="polizas-nueva" onClick={() => setCapturando(true)} disabled={cerrado}>
            Nueva póliza
          </button>
        </span>
      </header>

      <div className="filtros" data-recorrido="polizas-periodos">
        {periodosDelAnio.length > 0 && (
          <div className="seg desplazable" role="radiogroup" aria-label="Periodo">
            {periodosDelAnio.map((p) => (
              <button key={`${p.anio}-${p.mes}`} type="button" className="seg-opcion"
                role="radio" aria-checked={p.mes === mes}
                onClick={() => setMes(p.mes)}>
                {MESES[p.mes - 1]} {p.anio}{' '}
                <span className="sutil">· {p.estado === 'Cerrado' ? 'cerrado' : 'abierto'}</span>
              </button>
            ))}
          </div>
        )}
        {/* El segmentado solo lista periodos que ya tienen movimientos; sin estos dos selectores
            no habría forma de pararse en un mes vacío, que es justo donde se captura la primera
            póliza del periodo. */}
        <label>
          Ir a
          <select className="compacta" style={{ width: 'auto', margin: 0 }} value={mes}
            aria-label="Mes" onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((nombre, i) => <option key={nombre} value={i + 1}>{nombre}</option>)}
          </select>
        </label>
        <select className="compacta" style={{ width: 'auto', margin: 0 }} value={anio}
          aria-label="Año" onChange={(e) => setAnio(Number(e.target.value))}>
          {[anio - 1, anio, anio + 1].filter((a, i, xs) => xs.indexOf(a) === i).map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      <div className="franja-kpi media">
        <div>
          <div className="kicker">Pólizas del periodo</div>
          <div className="kpi-cifra">{vigentes.length}</div>
          <div className="kpi-pie">
            I {porTipo('Ingreso')} · E {porTipo('Egreso')} · D {porTipo('Diario')}
          </div>
        </div>
        <div>
          <div className="kicker">Cargos = Abonos</div>
          <div className={`kpi-cifra${cuadra ? '' : ' alerta'}`}>${dinero(cargosDelPeriodo)}</div>
          <div className={`kpi-pie${cuadra ? '' : ' alerta'}`}>
            {cuadra ? 'partida doble ✓ (RN-01)' : `abonos $${dinero(abonosDelPeriodo)} · no cuadra (RN-01)`}
          </div>
        </div>
        <div>
          <div className="kicker">CFDI sin contabilizar</div>
          <div className={`kpi-cifra${pendientes ? ' alerta' : ''}`}>{pendientes ?? '—'}</div>
          <div className="kpi-pie">
            {pendientes === undefined ? (
              'no se pudo consultar'
            ) : pendientes === 0 ? (
              'ninguno pendiente'
            ) : (
              <Link to={`/clientes/${clienteId}/contabilizacion`}>ir a contabilización →</Link>
            )}
          </div>
        </div>
        <div>
          <div className="kicker">Pólizas sin UUID</div>
          <div className={`kpi-cifra${sinUuid > 0 ? ' alerta' : ''}`}>{sinUuid}</div>
          <div className="kpi-pie">{sinUuid > 0 ? 'sin CFDI asociado (RF-403)' : 'todas con CFDI'}</div>
        </div>
      </div>


      {error && <p className="error" role="alert">{error}</p>}

      {polizas.length === 0 ? (
        <p className="vacio">Sin pólizas en este periodo. El periodo se abre solo al registrar la primera.</p>
      ) : (
        <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th>Folio</th><th>Tipo</th><th>Fecha</th><th>Concepto</th>
              <th style={{ textAlign: 'right' }}>Cargos</th>
              <th style={{ textAlign: 'right' }}>Abonos</th>
              <th>CFDI</th><th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {polizas.map((p) => {
              const estado = estadoDePoliza(p, sustituidas, uuidsCancelados)
              const conCfdiCancelado = p.cfdis.some((c) => uuidsCancelados.has(c.uuid))
              return (
              <tr key={p.id} onClick={() => setAbierta(p)}
                tabIndex={0} role="button"
                aria-label={`Ver el detalle de la póliza ${p.folioVisible}, ${p.concepto}. ${estado.titulo}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAbierta(p) } }}
                className={conCfdiCancelado ? 'resaltada' : undefined}
                style={{ cursor: 'pointer', opacity: p.estado === 'Cancelada' ? 0.5 : 1 }}>
                <td className="mono" style={{ fontWeight: 600 }}>{p.folioVisible}</td>
                <td>{p.tipo}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fechaCorta(p.fecha)}</td>
                <td>{p.sustituyeAPolizaId ? `${p.concepto} · corrección` : p.concepto}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{dinero(p.totalCargos)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{dinero(p.totalAbonos)}</td>
                <td>
                  {p.cfdis.length === 0 ? (
                    <span className="sutil">—</span>
                  ) : conCfdiCancelado ? (
                    <span className="insignia urgente" title="El SAT canceló un CFDI de esta póliza (RF-505).">
                      UUID cancelado
                    </span>
                  ) : (
                    <span className="insignia suave">{p.cfdis.length} UUID</span>
                  )}
                </td>
                <td>
                  <span className={`insignia ${estado.clase}`} title={estado.titulo}>{estado.texto}</span>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      )}

      <p className="nota">
        {polizas.length > 0 && `Mostrando ${polizas.length} ${polizas.length === 1 ? 'póliza' : 'pólizas'}${
          polizas.length - vigentes.length > 0 ? `, ${polizas.length - vigentes.length} sin vigencia` : ''
        } · folio consecutivo por tipo · `}
        Σ cargos = Σ abonos en cada póliza (RN-01); un periodo cerrado no acepta movimientos (RN-06).
        {cerrado && ` ${MESES[mes - 1]} ${anio}: cerrado e inmutable · reabrirlo es exclusivo del administrador y queda en la bitácora con quién lo hizo y cuándo.`}
      </p>

      {confirmandoCierre && (
        <CierreDePeriodo
          clienteId={clienteId}
          anio={anio}
          mes={mes}
          polizas={polizas}
          alCerrar={(mensaje) => {
            setConfirmandoCierre(false)
            if (mensaje) {
              avisar(mensaje)
              void recargar()
            }
          }}
        />
      )}

      {capturando && (
        <CapturaDePoliza
          clienteId={clienteId}
          anio={anio}
          mes={mes}
          nombreCliente={cliente.razonSocial}
          alCerrar={(mensaje) => {
            setCapturando(false)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {cancelando && (
        <CancelacionDePoliza
          clienteId={clienteId}
          poliza={cancelando}
          alCerrar={(mensaje) => {
            setCancelando(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {corrigiendo && (
        <CapturaDePoliza
          clienteId={clienteId}
          anio={anio}
          mes={mes}
          nombreCliente={cliente.razonSocial}
          correccionDe={corrigiendo}
          alCerrar={(mensaje) => {
            setCorrigiendo(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {abierta && (
        <div className="modal" {...telon(() => setAbierta(null))}>
          <div
            ref={panelDetalleRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTituloDetalle}
            className="panel"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(640px, 94vw)' }}
          >
            <h2 id={idTituloDetalle}>{abierta.folioVisible} · {abierta.concepto}</h2>
            <p className="sutil">
              {abierta.tipo} · {abierta.fecha}
              {abierta.cfdis.length > 0 && <> · CFDI: {abierta.cfdis.map((c) => c.uuid).join(', ')}</>}
            </p>
            {abierta.estado === 'Cancelada' && (
              <p className="error" role="alert">Cancelada: {abierta.motivoCancelacion}. La póliza se conserva íntegra como rastro (RF-406).</p>
            )}
            {abierta.sustituyeAPolizaId && (
              <p className="sutil">
                Corrección de otra póliza ·{' '}
                <button className="enlace" disabled={cargandoOriginal} onClick={() => void verOriginal(abierta.sustituyeAPolizaId!)}>
                  {cargandoOriginal ? 'Abriendo…' : 'Ver póliza original'}
                </button>
              </p>
            )}
            <table className="tabla">
              <thead>
                <tr><th>Cuenta</th><th style={{ textAlign: 'right' }}>Debe</th><th style={{ textAlign: 'right' }}>Haber</th></tr>
              </thead>
              <tbody>
                {abierta.movimientos.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <span className="mono">{m.cuentaNumero}</span> {m.cuentaNombre}{m.descripcion ? ` · ${m.descripcion}` : ''}
                      {m.etiquetaFiscal && (
                        <span className="sutil"> · {ETIQUETAS_FISCALES.find((et) => et.valor === m.etiquetaFiscal)?.texto}</span>
                      )}
                      {detalleDePago(m) && <div className="sutil" style={{ margin: 0 }}>{detalleDePago(m)}</div>}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>{m.cargo ? dinero(m.cargo) : ''}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{m.abono ? dinero(m.abono) : ''}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ fontWeight: 700 }}>Sumas</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(abierta.totalCargos)}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(abierta.totalAbonos)}</td>
                </tr>
              </tbody>
            </table>

            {abierta.congruenciaFiscal && (
              <>
                <p className={abierta.congruenciaFiscal.todoCongruente ? 'aviso' : 'error'} style={{ marginBottom: 4 }}>
                  <strong>Cotejo fiscal contra el CFDI (RF-604):</strong>{' '}
                  {abierta.congruenciaFiscal.todoCongruente
                    ? 'los importes etiquetados coinciden con el desglose del XML.'
                    : 'hay diferencias contra el desglose del XML.'}
                </p>
                <table className="tabla">
                  <thead>
                    <tr><th>Concepto</th><th style={{ textAlign: 'right' }}>Según CFDI</th><th style={{ textAlign: 'right' }}>En la póliza</th><th style={{ textAlign: 'right' }}>Diferencia</th></tr>
                  </thead>
                  <tbody>
                    {abierta.congruenciaFiscal.buckets.map((b) => (
                      <tr key={b.concepto}>
                        <td>{b.concepto}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{dinero(b.segunCfdi)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{dinero(b.registradoEnPoliza)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>
                          <span className={`insignia ${b.congruente ? 'vigente' : 'vencido'}`}>{dinero(b.diferencia)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div className="acciones">
              {abierta.estado === 'Vigente' && !cerrado && (
                <>
                  <button onClick={() => { setCancelando(abierta); setAbierta(null) }}>Cancelar póliza…</button>
                  <button onClick={() => { setCorrigiendo(abierta); setAbierta(null) }}>Corregir…</button>
                </>
              )}
              <button onClick={() => setAbierta(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/** Cada comprobación previa al cierre: cómo salió y qué decirle al contador. */
interface Comprobacion {
  clave: string
  estado: 'ok' | 'atencion'
  texto: string
}

/** Una llamada que puede haber fallado sin que eso invalide a las demás. */
type Resultado<T> = { dato: T } | { fallo: string }

function resolver<T>(r: PromiseSettledResult<T>): Resultado<T> {
  if (r.status === 'fulfilled') return { dato: r.value }
  return { fallo: r.reason instanceof Error ? r.reason.message : 'No se pudo consultar.' }
}

/**
 * Confirmación de cierre de periodo (RF-404, RN-06).
 *
 * Cerrar deja el periodo sin aceptar un solo movimiento más, y reabrirlo queda registrado
 * en la bitácora. Hasta ahora eso pasaba con un clic y sin decir nada: el contador no tenía
 * cómo ver, en el momento de decidir, si la balanza cuadraba o si quedaban CFDI sueltos.
 *
 * Las comprobaciones informan; no bloquean. El servidor acepta el cierre igual (no valida
 * ninguna de estas cosas), así que deshabilitar el botón sería un candado que se abre
 * llamando a la API de frente. Lo que sí garantiza esta pantalla es que nadie cierre un
 * periodo sin haber visto en qué estado está.
 */
function CierreDePeriodo({
  clienteId,
  anio,
  mes,
  polizas,
  alCerrar,
}: {
  clienteId: string
  anio: number
  mes: number
  polizas: Poliza[]
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const [remoto, setRemoto] = useState<{
    balanza: Resultado<Balanza>
    congruencia: Resultado<CongruenciaGlobal>
    panel: Resultado<ResumenDePanel>
  } | null>(null)
  const [conPaquete, setConPaquete] = useState(true)
  const [error, setError] = useState('')
  const [cerrando, setCerrando] = useState(false)
  // El gancho refresca este callback en cada render, así que lee el `cerrando` vigente:
  // Escape no saca del modal mientras el cierre está en vuelo.
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, () => {
    if (!cerrando) alCerrar()
  })

  useEffect(() => {
    let vigente = true
    async function comprobar() {
      // allSettled y no all: la congruencia responde 400 cuando el catálogo tiene cuentas
      // con movimientos y sin código agrupador, y ese es justo el caso que hay que enseñar.
      // Con Promise.all, el cliente con el catálogo incompleto vería el modal en blanco.
      // Con un plazo: `allSettled` espera a la última respuesta, y una petición colgada dejaría
      // el modal esperando indefinidamente sin forma de cerrar el periodo. Lo que no llegue en
      // diez segundos se cuenta como no comprobado, que es distinto de comprobado y correcto.
      const conPlazo = <T,>(promesa: Promise<T>) =>
        Promise.race([
          promesa,
          new Promise<never>((_, rechazar) =>
            setTimeout(() => rechazar(new Error('La consulta tardó demasiado.')), 10_000),
          ),
        ])

      const [balanza, congruencia, panel] = await Promise.allSettled([
        conPlazo(api.balanza(clienteId, anio, mes)),
        conPlazo(api.congruenciaGlobal(clienteId, anio, mes)),
        conPlazo(api.panelResumen(clienteId, anio, mes)),
      ])
      if (!vigente) return
      setRemoto({
        balanza: resolver(balanza),
        congruencia: resolver(congruencia),
        panel: resolver(panel),
      })
    }
    void comprobar()
    return () => {
      vigente = false
    }
  }, [clienteId, anio, mes])

  const vigentes = polizas.filter((p) => p.estado === 'Vigente')
  const cargos = vigentes.reduce((suma, p) => suma + p.totalCargos, 0)
  const abonos = vigentes.reduce((suma, p) => suma + p.totalAbonos, 0)
  const sinUuid = vigentes.filter((p) => p.cfdis.length === 0).length

  function comprobaciones(): Comprobacion[] {
    if (!remoto) return []
    const cuadra = Math.abs(cargos - abonos) < 0.005
    const cuantas = `${vigentes.length} ${vigentes.length === 1 ? 'póliza vigente' : 'pólizas vigentes'}`
    const lista: Comprobacion[] = [
      vigentes.length === 0
        ? {
            // Cero contra cero cuadra por aritmética, pero decir «partida doble verificada»
            // sobre un periodo vacío es afirmar algo que nadie comprobó.
            clave: 'partida-doble',
            estado: 'atencion',
            texto: 'El periodo no tiene ninguna póliza vigente que cerrar.',
          }
        : {
            clave: 'partida-doble',
            // Se suma sobre las pólizas del periodo y no sobre la balanza: la balanza agrupa
            // por rango de fechas y solo cuentas de registro, así que responde otra pregunta.
            estado: cuadra ? 'ok' : 'atencion',
            texto: cuadra
              ? `Partida doble en ${cuantas}: cargos y abonos suman $${dinero(cargos)} (RN-01).`
              : `Las pólizas del periodo no cuadran: cargos $${dinero(cargos)} contra abonos $${dinero(abonos)} (RN-01).`,
          },
    ]

    lista.push(
      'dato' in remoto.balanza
        ? {
            clave: 'balanza',
            estado: remoto.balanza.dato.cuadrada ? 'ok' : 'atencion',
            texto: remoto.balanza.dato.cuadrada
              ? 'Balanza de comprobación cuadrada (RF-701).'
              : `Balanza descuadrada: cargos $${dinero(remoto.balanza.dato.totalCargos)} contra abonos $${dinero(remoto.balanza.dato.totalAbonos)} (RF-701).`,
          }
        : { clave: 'balanza', estado: 'atencion', texto: `No se pudo comprobar la balanza: ${remoto.balanza.fallo}` },
    )

    lista.push(
      'dato' in remoto.congruencia
        ? {
            clave: 'congruencia',
            estado: remoto.congruencia.dato.activoIgualPasivoMasCapital ? 'ok' : 'atencion',
            texto: remoto.congruencia.dato.activoIgualPasivoMasCapital
              ? `Congruencia global: activo = pasivo + capital = $${dinero(remoto.congruencia.dato.totalActivo)} (RF-706).`
              : `El balance no cuadra: activo $${dinero(remoto.congruencia.dato.totalActivo)} contra pasivo + capital $${dinero(remoto.congruencia.dato.totalPasivoMasCapital)} (RF-706).`,
          }
        : { clave: 'congruencia', estado: 'atencion', texto: `No se pudo comprobar la congruencia global: ${remoto.congruencia.fallo}` },
    )

    return lista
  }

  function advertencias(): string[] {
    const avisos: string[] = []
    if (remoto && 'fallo' in remoto.panel) {
      // Callar aquí sería lo peor de las tres: como el estado «todo bien» es no mostrar nada,
      // un fallo silencioso se lee exactamente igual que «no queda ningún CFDI suelto».
      avisos.push(`No se pudo comprobar cuántos CFDI quedan sin contabilizar: ${remoto.panel.fallo}`)
    }
    if (remoto && 'dato' in remoto.panel && remoto.panel.dato.pendientes > 0) {
      const n = remoto.panel.dato.pendientes
      avisos.push(`${n} CFDI del periodo ${n === 1 ? 'sigue' : 'siguen'} sin contabilizar; al cerrar ya no podrás registrarlos aquí.`)
    }
    if (sinUuid > 0) {
      avisos.push(`${sinUuid} ${sinUuid === 1 ? 'póliza vigente no tiene' : 'pólizas vigentes no tienen'} CFDI asociado (RF-403).`)
    }
    return avisos
  }

  async function confirmar() {
    // El botón lleva aria-disabled en vez de disabled para no perder el foco al pulsarlo, así
    // que la guarda de verdad vive aquí: sin ella, un segundo Enter mandaría un segundo POST
    // y el servidor respondería PERIODO_YA_CERRADO sobre un cierre que sí funcionó.
    if (!remoto || cerrando) return
    setError('')
    setCerrando(true)
    try {
      await api.cerrarPeriodo(clienteId, anio, mes)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cerrar el periodo.')
      setCerrando(false)
      return
    }
    // A partir de aquí el periodo YA está cerrado. Si el paquete falla, el aviso tiene que
    // decir las dos cosas: que el cierre sí ocurrió y que el ZIP no.
    let nota = ''
    if (conPaquete) {
      try {
        await api.descargarPaqueteDeCierre(clienteId, anio, mes)
        nota = ' Se descargó el paquete de cierre.'
      } catch {
        nota = ' El paquete de cierre no se pudo generar; descárgalo desde Reportes.'
      }
    }
    alCerrar(`Periodo ${MESES[mes - 1]} ${anio} cerrado: ya no acepta movimientos (RN-06).${nota}`)
  }

  const avisos = advertencias()

  // Descartar mientras el cierre está en vuelo dejaba el periodo cerrado en el servidor y la
  // pantalla diciendo que seguía abierto. Mientras `cerrando`, ni el fondo ni Escape ni
  // «Cancelar» sacan del modal: la única salida es que la llamada termine.
  const descartar = () => {
    if (!cerrando) alCerrar()
  }

  return (
    <div className="modal" {...telon(() => descartar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-busy={!remoto}
        className="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo}>Cerrar periodo · {MESES[mes - 1]} {anio}</h2>

        {/* La región viva se monta desde el primer render y solo cambia de contenido: si se
            montara al llegar el resultado, el lector de pantalla no anunciaría nada, porque
            solo lee los cambios dentro de una región que ya estaba ahí. */}
        <div role="status" aria-live="polite" style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {!remoto ? (
            <p className="sutil" style={{ margin: 0 }}>Comprobando el periodo…</p>
          ) : (
            <>
              {comprobaciones().map((c) => (
                <p key={c.clave} className={c.estado === 'ok' ? 'comprobacion' : 'comprobacion pendiente'}>
                  <span aria-hidden="true">{c.estado === 'ok' ? '✓' : '⚠'}</span>
                  <span>{c.texto}</span>
                </p>
              ))}

              {avisos.length > 0 && (
                <div className="aviso-critico">
                  <div>
                    {avisos.map((texto) => (
                      <p key={texto} style={{ margin: 0 }}>{texto}</p>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <label className="casilla">
          <input type="checkbox" checked={conPaquete} onChange={(e) => setConPaquete(e.target.checked)} />
          Generar el paquete de cierre: balanza, reportes y XML del periodo (RF-903)
        </label>

        <p className="sutil">
          Un periodo cerrado es inmutable (RN-06). Se puede reabrir, pero la reapertura queda
          registrada en la bitácora con quién la hizo y cuándo.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={descartar} disabled={cerrando}>Cancelar</button>
          {/* aria-disabled y no disabled: al pulsar «Cerrar periodo» el botón se desactivaría
              con el foco encima, y el foco saltaría fuera de la trampa, al principio del
              documento. Así el foco se queda donde está y la guarda vive en confirmar(). */}
          <button
            type="button"
            className="primario"
            onClick={() => void confirmar()}
            aria-disabled={!remoto || cerrando}
          >
            {cerrando ? 'Cerrando…' : 'Cerrar periodo'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Cancelación con motivo obligatorio: nada se borra, todo queda en el rastro (RF-406, C-06). */
function CancelacionDePoliza({
  clienteId,
  poliza,
  alCerrar,
}: {
  clienteId: string
  poliza: Poliza
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function cancelar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const motivo = String(new FormData(evento.currentTarget).get('motivo'))
    setError('')
    setEnviando(true)
    try {
      await api.cancelarPoliza(clienteId, poliza.id, motivo)
      alCerrar(`Póliza ${poliza.folioVisible} cancelada; se conserva íntegra con su motivo en la bitácora.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cancelar la póliza.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={cancelar}
      >
        <h2 id={idTitulo}>Cancelar póliza {poliza.folioVisible}</h2>
        <p className="sutil">
          La póliza no se borra: queda marcada como cancelada con su folio, movimientos y
          motivo. Si necesitas el asiento corregido, usa «Corregir…» que además registra la sustituta.
        </p>
        <label>
          Motivo de la cancelación (obligatorio)
          <input name="motivo" required maxLength={500} placeholder="Importe capturado en póliza equivocada" />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Volver</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Cancelando…' : 'Cancelar póliza'}
          </button>
        </div>
      </form>
    </div>
  )
}

interface Renglon {
  cuentaId: string
  cargo: string
  abono: string
  descripcion: string
  moneda: string
  tipoCambio: string
  metodoDePago: MetodoDePago | ''
  banco: string
  cuenta: string
  referencia: string
  rfcTercero: string
  nombreTercero: string
  bancoCodigoSat: string
  etiquetaFiscal: EtiquetaFiscalMovimiento | ''
  /** Solo de pantalla: si el pliegue de datos de pago de este renglón está abierto. */
  pagoAbierto: boolean
}

const RENGLON_VACIO: Renglon = {
  cuentaId: '', cargo: '', abono: '', descripcion: '', moneda: 'MXN', tipoCambio: '',
  metodoDePago: '', banco: '', cuenta: '', referencia: '',
  rfcTercero: '', nombreTercero: '', bancoCodigoSat: '', etiquetaFiscal: '', pagoAbierto: false,
}

/**
 * Lo que el pliegue de datos de pago enseña cuando está cerrado. Sin esto, una corrección que
 * llega con banco y beneficiario capturados los escondería sin dejar rastro en pantalla.
 */
function resumenDePago(renglon: Renglon): string {
  const partes = [
    renglon.moneda && renglon.moneda !== 'MXN' ? renglon.moneda : '',
    renglon.metodoDePago,
    renglon.banco,
    renglon.nombreTercero,
    renglon.descripcion,
  ].filter(Boolean)
  return partes.length > 0 ? `· ${partes.join(' · ')}` : ''
}

/**
 * Captura con partida doble en vivo: el registro se bloquea mientras haya descuadre (C-02).
 * Con `correccionDe`, cancela la original y registra la sustituta ligada a ella (RF-406, C-06).
 * `uuidsPrefill`/`conceptoPrefill` la abren precargada desde el panel de contabilización (RF-601).
 */
export function CapturaDePoliza({
  clienteId,
  anio,
  mes,
  correccionDe,
  uuidsPrefill,
  conceptoPrefill,
  fechaPrefill,
  movimientosPrefill,
  nombreCliente,
  alCerrar,
}: {
  clienteId: string
  anio: number
  mes: number
  nombreCliente?: string
  correccionDe?: Poliza
  uuidsPrefill?: string[]
  conceptoPrefill?: string
  fechaPrefill?: string
  movimientosPrefill?: { cuentaId: string; cargo: number; abono: number; etiquetaFiscal?: EtiquetaFiscalMovimiento }[]
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>([])
  const [tipo, setTipo] = useState<TipoPoliza>(correccionDe?.tipo ?? 'Diario')
  const [fecha, setFecha] = useState(() => {
    if (correccionDe) return correccionDe.fecha
    if (fechaPrefill) return fechaPrefill
    const hoy = new Date()
    const dia = hoy.getFullYear() === anio && hoy.getMonth() + 1 === mes ? hoy.getDate() : 1
    return `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
  })
  const [concepto, setConcepto] = useState(correccionDe?.concepto ?? conceptoPrefill ?? '')
  const [motivo, setMotivo] = useState('')
  const [uuids, setUuids] = useState<string[]>(() => correccionDe?.cfdis.map((c) => c.uuid) ?? uuidsPrefill ?? [])
  const [uuidManual, setUuidManual] = useState('')
  const [cfdiDelPeriodo, setCfdiDelPeriodo] = useState<CfdiDelPanel[]>([])
  const [renglones, setRenglones] = useState<Renglon[]>(() => {
    if (correccionDe) {
      return correccionDe.movimientos.map((m) => ({
        cuentaId: m.cuentaId,
        cargo: m.cargo ? String(m.cargo) : '',
        abono: m.abono ? String(m.abono) : '',
        descripcion: m.descripcion ?? '',
        moneda: m.moneda || 'MXN',
        tipoCambio: m.tipoCambio ? String(m.tipoCambio) : '',
        pagoAbierto: !!(m.metodoDePago || m.descripcion || (m.moneda && m.moneda !== 'MXN')),
        metodoDePago: m.metodoDePago ?? '',
        banco: m.banco ?? '',
        cuenta: m.cuenta ?? '',
        referencia: m.referencia ?? '',
        rfcTercero: m.rfcTercero ?? '',
        nombreTercero: m.nombreTercero ?? '',
        bancoCodigoSat: m.bancoCodigoSat ?? '',
        etiquetaFiscal: m.etiquetaFiscal ?? '',
      }))
    }
    if (movimientosPrefill && movimientosPrefill.length > 0) {
      return movimientosPrefill.map((m) => ({
        ...RENGLON_VACIO,
        cuentaId: m.cuentaId,
        cargo: m.cargo ? String(m.cargo) : '',
        abono: m.abono ? String(m.abono) : '',
        etiquetaFiscal: m.etiquetaFiscal ?? '',
      }))
    }
    return [
      { ...RENGLON_VACIO },
      { ...RENGLON_VACIO },
    ]
  })
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.catalogoDeCuentas(clienteId)
      .then((todas) => setCuentas(todas.filter((c) => c.esDeRegistro && c.estado === 'Activa')))
      .catch(() => setCuentas([]))
  }, [clienteId])

  // El repositorio del periodo solo sirve para elegir de una lista en vez de teclear un UUID
  // de 36 caracteres. Si no carga, el campo manual sigue ahí y la captura no se bloquea.
  useEffect(() => {
    api.panelListarCfdi(clienteId, anio, mes)
      .then(setCfdiDelPeriodo)
      .catch(() => setCfdiDelPeriodo([]))
  }, [clienteId, anio, mes])

  function asociar(uuid: string) {
    const limpio = uuid.trim()
    if (limpio && !uuids.includes(limpio)) setUuids((u) => [...u, limpio])
  }

  // Lo que se puede asociar es lo del periodo que todavía no está en la póliza.
  const disponibles = cfdiDelPeriodo.filter((c) => !uuids.includes(c.uuid))
  const totalCargos = renglones.reduce((s, r) => s + (Number(r.cargo) || 0), 0)
  const totalAbonos = renglones.reduce((s, r) => s + (Number(r.abono) || 0), 0)
  const diferencia = Math.round((totalCargos - totalAbonos) * 100) / 100
  const descuadrada = diferencia !== 0 || totalCargos === 0

  function actualizar(indice: number, cambio: Partial<Renglon>) {
    setRenglones((r) => r.map((renglon, i) => (i === indice ? { ...renglon, ...cambio } : renglon)))
  }

  async function registrar() {
    setError('')
    setEnviando(true)
    try {
      const datos = {
        tipo,
        fecha,
        concepto,
        movimientos: renglones
          .filter((r) => r.cuentaId && (Number(r.cargo) > 0 || Number(r.abono) > 0))
          .map((r) => ({
            cuentaId: r.cuentaId,
            cargo: Number(r.cargo) || 0,
            abono: Number(r.abono) || 0,
            descripcion: r.descripcion.trim() || undefined,
            moneda: r.moneda || 'MXN',
            tipoCambio: r.moneda !== 'MXN' ? Number(r.tipoCambio) || undefined : undefined,
            metodoDePago: r.metodoDePago || undefined,
            banco: r.banco.trim() || undefined,
            cuenta: r.cuenta.trim() || undefined,
            referencia: r.referencia.trim() || undefined,
            rfcTercero: r.rfcTercero.trim() || undefined,
            nombreTercero: r.nombreTercero.trim() || undefined,
            bancoCodigoSat: r.bancoCodigoSat || undefined,
            etiquetaFiscal: r.etiquetaFiscal || undefined,
          })),
        uuids,
      }

      const poliza = correccionDe
        ? await api.corregirPoliza(clienteId, correccionDe.id, motivo, datos)
        : await api.registrarPoliza(clienteId, datos)

      alCerrar(
        correccionDe
          ? `${correccionDe.folioVisible} cancelada y sustituida por ${poliza.folioVisible}; el vínculo quedó en el rastro.`
          : `Póliza ${poliza.folioVisible} registrada: cargos = abonos = ${dinero(poliza.totalCargos)}.`,
      )
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar la póliza.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(700px, 94vw)' }}
      >
        <h2 id={idTitulo}>
          {correccionDe
            ? `Corregir ${correccionDe.folioVisible} · se cancela y se registra la sustituta`
            : 'Nueva póliza'}
        </h2>
        <p className="sutil">
          {nombreCliente && `${nombreCliente} · `}{MESES[mes - 1]} {anio} abierto · folio consecutivo por tipo
        </p>

        {correccionDe && (
          <label>
            Motivo de la corrección (obligatorio, queda en el rastro)
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} required maxLength={500}
              placeholder="Importe equivocado en el cargo a combustibles" />
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <label>
            Tipo
            <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPoliza)}>
              <option>Diario</option><option>Ingreso</option><option>Egreso</option>
            </select>
          </label>
          <label>
            Fecha (dentro del periodo)
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </label>
        </div>

        <label>
          Concepto
          <input value={concepto} onChange={(e) => setConcepto(e.target.value)} required maxLength={500}
            placeholder="Pago de renta de oficina · agosto" />
        </label>

        <fieldset className="caja-cfdi">
          <legend>
            CFDI asociados (opcional, RF-403){' '}
            {uuids.length > 0 && (
              <span className="insignia suave">{uuids.length} UUID</span>
            )}
          </legend>

          {uuids.length === 0 ? (
            <p className="sutil" style={{ margin: 0 }}>Ninguno asociado todavía.</p>
          ) : (
            <ul className="chips">
              {uuids.map((u) => (
                <li key={u} className="insignia neutra">
                  <span className="mono" title={u}>{uuidCorto(u)}</span>
                  <button type="button" className="enlace" aria-label={`Quitar el CFDI ${u}`}
                    onClick={() => setUuids((lista) => lista.filter((x) => x !== u))}>×</button>
                </li>
              ))}
            </ul>
          )}

          <div className="asociar-cfdi">
            {disponibles.length > 0 && (
              <label>
                Del repositorio del periodo
                <select value="" onChange={(e) => asociar(e.target.value)}>
                  <option value="">+ asociar…</option>
                  {disponibles.map((c) => (
                    <option key={c.uuid} value={c.uuid}>
                      {uuidCorto(c.uuid)} · {c.nombreEmisor} · ${dinero(c.total)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              {disponibles.length > 0 ? 'O teclear el UUID' : 'UUID del CFDI'}
              <span style={{ display: 'flex', gap: 6 }}>
                <input value={uuidManual} onChange={(e) => setUuidManual(e.target.value)} className="mono"
                  placeholder="a1b2c3d4-e5f6-7890-abcd-ef1234567890" maxLength={36}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); asociar(uuidManual); setUuidManual('') }
                  }} />
                <button type="button" disabled={!uuidManual.trim()}
                  onClick={() => { asociar(uuidManual); setUuidManual('') }}>Asociar</button>
              </span>
            </label>
          </div>
        </fieldset>

        <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th style={{ minWidth: 190 }}>Cuenta</th><th style={{ width: 110 }}>Debe</th><th style={{ width: 110 }}>Haber</th>
              <th style={{ width: 150 }}>Impuesto</th><th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((renglon, i) => (
              <Fragment key={i}>
                <tr>
                  <td>
                    <select value={renglon.cuentaId} onChange={(e) => actualizar(i, { cuentaId: e.target.value })}
                      style={{ width: '100%', padding: '6px 8px', fontFamily: 'inherit', fontSize: 13 }}>
                      <option value="">— cuenta de registro —</option>
                      {cuentas.map((c) => (
                        <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>
                      ))}
                    </select>
                  </td>
                  <td><input inputMode="decimal" placeholder="0.00" value={renglon.cargo}
                    onChange={(e) => actualizar(i, { cargo: e.target.value, abono: e.target.value ? '' : renglon.abono })}
                    style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                  <td><input inputMode="decimal" placeholder="0.00" value={renglon.abono}
                    onChange={(e) => actualizar(i, { abono: e.target.value, cargo: e.target.value ? '' : renglon.cargo })}
                    style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                  <td>
                    <select value={renglon.etiquetaFiscal}
                      onChange={(e) => actualizar(i, { etiquetaFiscal: e.target.value as EtiquetaFiscalMovimiento | '' })}
                      style={{ width: '100%', padding: '6px 8px', fontFamily: 'inherit', fontSize: 13 }}>
                      <option value="">—</option>
                      {ETIQUETAS_FISCALES.map((et) => (
                        <option key={et.valor} value={et.valor}>{et.texto}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="enlace" disabled={renglones.length <= 2}
                      aria-label={`Eliminar el movimiento ${i + 1}`}
                      title={renglones.length <= 2
                        ? 'Una póliza necesita al menos dos movimientos para cuadrar (RN-01).'
                        : 'Eliminar movimiento'}
                      onClick={() => setRenglones((r) => r.filter((_, j) => j !== i))}>×</button>
                  </td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ paddingTop: 0, paddingBottom: 10 }}>
                    <details className="pliegue-pago" open={renglon.pagoAbierto}
                      onToggle={(e) => actualizar(i, { pagoAbierto: e.currentTarget.open })}>
                    <summary>
                      Datos de pago, moneda y tercero{' '}
                      <span className="sutil">{resumenDePago(renglon) || '· opcional (RF-403, RN-12, RF-803)'}</span>
                    </summary>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', paddingTop: 6 }}>
                      <input value={renglon.descripcion} onChange={(e) => actualizar(i, { descripcion: e.target.value })}
                        placeholder="Descripción del movimiento (opcional, RF-401)" maxLength={300}
                        style={{ flex: '1 1 220px', padding: '4px 8px', fontSize: 12 }} />
                      <input value={renglon.moneda}
                        onChange={(e) => actualizar(i, { moneda: e.target.value.toUpperCase().slice(0, 3) })}
                        placeholder="MXN" maxLength={3} title="Moneda (ISO 4217, RN-12)" className="mono"
                        style={{ width: 52, padding: '4px 6px', fontSize: 12, textTransform: 'uppercase' }} />
                      {renglon.moneda !== 'MXN' && (
                        <input inputMode="decimal" value={renglon.tipoCambio}
                          onChange={(e) => actualizar(i, { tipoCambio: e.target.value })}
                          placeholder="Tipo de cambio" title="Tipo de cambio del comprobante (C-07)"
                          style={{ width: 100, padding: '4px 6px', fontSize: 12 }} />
                      )}
                      {renglon.moneda !== 'MXN' && <AyudaDeCampo clave="tipo-de-cambio" />}
                      <select value={renglon.metodoDePago}
                        onChange={(e) => actualizar(i, { metodoDePago: e.target.value as MetodoDePago | '' })}
                        title="Datos de pago (opcional, C-04)" style={{ padding: '4px 6px', fontSize: 12 }}>
                        <option value="">Sin dato de pago</option>
                        {METODOS_DE_PAGO.map((m) => <option key={m.valor} value={m.valor}>{m.texto}</option>)}
                      </select>
                      {renglon.metodoDePago && renglon.metodoDePago !== 'Efectivo' && (
                        <>
                          <input value={renglon.banco} onChange={(e) => actualizar(i, { banco: e.target.value })}
                            placeholder="Banco" maxLength={100} style={{ width: 110, padding: '4px 6px', fontSize: 12 }} />
                          <input value={renglon.cuenta} onChange={(e) => actualizar(i, { cuenta: e.target.value })}
                            placeholder="Cuenta/cheque" maxLength={50} className="mono"
                            style={{ width: 110, padding: '4px 6px', fontSize: 12 }} />
                        </>
                      )}
                      {renglon.metodoDePago && (
                        <input value={renglon.referencia} onChange={(e) => actualizar(i, { referencia: e.target.value })}
                          placeholder="Referencia" maxLength={100} style={{ width: 120, padding: '4px 6px', fontSize: 12 }} />
                      )}
                      {(renglon.metodoDePago === 'Cheque' || renglon.metodoDePago === 'Transferencia') && (
                        <>
                          <select value={renglon.bancoCodigoSat}
                            onChange={(e) => actualizar(i, { bancoCodigoSat: e.target.value })}
                            title="Código de banco del catálogo c_Banco del SAT (RF-803)"
                            style={{ padding: '4px 6px', fontSize: 12 }}>
                            <option value="">Sin código de banco SAT</option>
                            {BANCOS_SAT.map((b) => <option key={b.codigo} value={b.codigo}>{b.texto}</option>)}
                          </select>
                          <input value={renglon.nombreTercero} onChange={(e) => actualizar(i, { nombreTercero: e.target.value })}
                            placeholder="Beneficiario / tercero" maxLength={300}
                            style={{ width: 160, padding: '4px 6px', fontSize: 12 }} />
                          <input value={renglon.rfcTercero}
                            onChange={(e) => actualizar(i, { rfcTercero: e.target.value.toUpperCase() })}
                            placeholder="RFC del tercero" maxLength={13} className="mono"
                            title="RFC del beneficiario/tercero, distinto del contribuyente (RF-803)"
                            style={{ width: 120, padding: '4px 6px', fontSize: 12, textTransform: 'uppercase' }} />
                        </>
                      )}
                    </div>
                    {(renglon.metodoDePago === 'Cheque' || renglon.metodoDePago === 'Transferencia') && (
                      <p className="sutil" style={{ margin: '4px 0 0' }}>
                        Con banco, beneficiario y RFC del tercero completos, el PL del Anexo 24
                        incluye el nodo {renglon.metodoDePago} de este movimiento (RF-803).
                      </p>
                    )}
                    </details>
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="total">
              <td style={{ fontWeight: 700 }}>Sumas · deben ser iguales (RN-01)</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalCargos)}</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalAbonos)}</td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        </div>

        <p className="sutil">
          El desglose de IVA 16 % / 0 % / exento y las retenciones se cotejan contra el CFDI
          asociado al registrar la póliza (RF-604).
        </p>

        <p>
          <button className="ghost compacta" type="button"
            onClick={() => setRenglones((r) => [...r, { ...RENGLON_VACIO }])}>
            + Agregar movimiento
          </button>
        </p>

        {diferencia !== 0 && (
          <p className="error" role="alert">
            <span aria-hidden="true">⚠ </span>
            <strong>Póliza descuadrada: diferencia de ${dinero(Math.abs(diferencia))}.</strong>{' '}
            El sistema no registra pólizas descuadradas (RN-01); las diferencias por redondeo se
            resuelven con un movimiento de ajuste explícito.
          </p>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button onClick={() => alCerrar()}>Descartar</button>
          <button className="primario"
            disabled={descuadrada || enviando || !concepto || (Boolean(correccionDe) && !motivo)}
            onClick={() => void registrar()}>
            {enviando ? 'Registrando…' : correccionDe ? 'Cancelar y registrar sustituta' : 'Registrar póliza'}
          </button>
        </div>
      </div>
    </div>
  )
}
